import { marked } from "marked";

export type FaqItem = { q: string; a: string };

export type InvestorProfile = "iniciante" | "investidor" | "endividado";

export type AprendaFrontmatter = {
  title: string;
  description: string;
  slug: string;
  publishedAt: string;
  updatedAt: string;
  readingMinutes: number;
  category: string;
  keywords?: string[];
  audience?: InvestorProfile[];
  faq?: FaqItem[];
};

export type AprendaPost = AprendaFrontmatter & {
  html: string;
  htmlBeforeCta: string;
  htmlAfterCta: string;
  toc: TocItem[];
};

export type TocItem = { id: string; text: string; level: 2 | 3 };

// Carrega todos os .md em build-time
const rawFiles = import.meta.glob("/src/content/aprenda/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

marked.setOptions({ gfm: true, breaks: false });

/** Minimal browser-safe frontmatter parser (gray-matter needs Node's Buffer). */
function stripQuotes(s: string): string {
  const t = s.trim();
  if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) {
    return t.slice(1, -1);
  }
  return t;
}
function parseScalar(v: string): unknown {
  const t = v.trim();
  if (t.startsWith("[") && t.endsWith("]")) {
    const inner = t.slice(1, -1).trim();
    if (!inner) return [];
    return inner.split(",").map((x) => stripQuotes(x.trim()));
  }
  if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t);
  return stripQuotes(t);
}
function parseFrontmatter(raw: string): { data: Record<string, unknown>; content: string } {
  if (!raw.startsWith("---")) return { data: {}, content: raw };
  const end = raw.indexOf("\n---", 3);
  if (end === -1) return { data: {}, content: raw };
  const fmBlock = raw.slice(3, end).replace(/^\r?\n/, "");
  const rest = raw.slice(end + 4).replace(/^\r?\n/, "");
  const data: Record<string, unknown> = {};
  const lines = fmBlock.split(/\r?\n/);
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim() || line.trim().startsWith("#")) { i++; continue; }
    const m = /^([A-Za-z0-9_-]+):\s*(.*)$/.exec(line);
    if (!m) { i++; continue; }
    const key = m[1];
    const rawVal = m[2];
    if (!rawVal) {
      const items: Record<string, string>[] = [];
      i++;
      let current: Record<string, string> | null = null;
      while (i < lines.length) {
        const next = lines[i];
        const dash = /^\s+-\s+([A-Za-z0-9_-]+):\s*(.*)$/.exec(next);
        const cont = /^\s{4,}([A-Za-z0-9_-]+):\s*(.*)$/.exec(next);
        if (dash) { current = { [dash[1]]: stripQuotes(dash[2]) }; items.push(current); i++; }
        else if (cont && current) { current[cont[1]] = stripQuotes(cont[2]); i++; }
        else if (next.trim() === "") { i++; }
        else { break; }
      }
      data[key] = items;
      continue;
    }
    data[key] = parseScalar(rawVal);
    i++;
  }
  return { data, content: rest };
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function addHeadingIds(html: string): { html: string; toc: TocItem[] } {
  const toc: TocItem[] = [];
  const used = new Map<string, number>();
  const out = html.replace(
    /<h([23])>([\s\S]*?)<\/h\1>/gi,
    (_m, lvl: string, inner: string) => {
      const text = inner.replace(/<[^>]+>/g, "").trim();
      let id = slugify(text);
      if (!id) return `<h${lvl}>${inner}</h${lvl}>`;
      const n = used.get(id) ?? 0;
      used.set(id, n + 1);
      if (n > 0) id = `${id}-${n + 1}`;
      toc.push({ id, text, level: Number(lvl) as 2 | 3 });
      return `<h${lvl} id="${id}">${inner}</h${lvl}>`;
    },
  );
  return { html: out, toc };
}

function splitHtml(html: string): { before: string; after: string } {
  // Insere CTA depois do 2º </h2>; se não tiver, depois da metade.
  const indices: number[] = [];
  const re = /<\/h2>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    indices.push(m.index + m[0].length);
  }
  let cut: number;
  if (indices.length >= 3) cut = indices[1];
  else if (indices.length >= 1) cut = indices[Math.floor(indices.length / 2)];
  else cut = Math.floor(html.length / 2);
  return { before: html.slice(0, cut), after: html.slice(cut) };
}

function buildPost(raw: string): AprendaPost {
  const { data, content } = parseFrontmatter(raw);
  const fm = data as AprendaFrontmatter;
  const rawHtml = marked.parse(content, { async: false }) as string;
  const { html, toc } = addHeadingIds(rawHtml);
  const { before, after } = splitHtml(html);
  return {
    ...fm,
    html,
    htmlBeforeCta: before,
    htmlAfterCta: after,
    toc,
  };
}

const POSTS: AprendaPost[] = Object.values(rawFiles)
  .map(buildPost)
  .sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));

export function getAllPosts(): AprendaPost[] {
  return POSTS;
}

export function getPostBySlug(slug: string): AprendaPost | null {
  return POSTS.find((p) => p.slug === slug) ?? null;
}

export function getRelatedPosts(slug: string, limit = 2): AprendaPost[] {
  return POSTS.filter((p) => p.slug !== slug).slice(0, limit);
}